import { APIKit } from "config/axios";
import store from "config/redux";
import { end, start } from "config/redux/slice";

import { RouteConstants, ServerConstants, ServiceConstants } from "constant";
import { ServiceModel } from "models";
import { HolidayListParams, HolidayRequest, HolidayResponse } from "types";
import { AppUtils } from "utils";

import { handleServiceError } from "./common";
import * as PublicService from "./public";

const { ROUTE_LOGIN } = RouteConstants;
const { API_V1_HOLIDAY } = ServerConstants;
const {
  REQUEST_HOLIDAY_LIST_ID,
  REQUEST_HOLIDAY_CREATE_ID,
  REQUEST_HOLIDAY_UPDATE_ID,
  REQUEST_HOLIDAY_DELETE_ID,
} = ServiceConstants;

const buildHolidayUpdateRequestId = (id: number) => `${REQUEST_HOLIDAY_UPDATE_ID}${id}`;
const buildHolidayDeleteRequestId = (id: number) => `${REQUEST_HOLIDAY_DELETE_ID}${id}`;

const buildHolidayUrl = (params?: HolidayListParams) => {
  const searchParams = new URLSearchParams();

  if (params?.dateFrom) {
    searchParams.append("dateFrom", params.dateFrom);
  }

  if (params?.dateTo) {
    searchParams.append("dateTo", params.dateTo);
  }

  const queryString = searchParams.toString();
  return queryString ? `${API_V1_HOLIDAY}?${queryString}` : API_V1_HOLIDAY;
};

async function retryWithRenewal<T>(request: () => Promise<T>, retryOnUnauthorized = true): Promise<T> {
  try {
    return await request();
  } catch (err: any) {
    const errStatus = JSON.parse(JSON.stringify(err))?.status;

    if (errStatus === 401 && retryOnUnauthorized) {
      const renewed = await PublicService.renew();

      if (renewed) {
        return retryWithRenewal(request, false);
      }

      AppUtils.logout();
      if (window.location.pathname !== ROUTE_LOGIN) {
        window.location.href = ROUTE_LOGIN;
      }
    }

    throw err;
  }
}

async function performTrackedRequest<T extends object | undefined>(
  requestId: string,
  request: () => Promise<T>,
): Promise<T> {
  try {
    store.dispatch(start(requestId));
    const data = await retryWithRenewal(request);
    store.dispatch(end(new ServiceModel(requestId, data)));
    return data;
  } catch (err: any) {
    handleServiceError(requestId, err);
    throw err;
  }
}

export async function holidayList(params?: HolidayListParams): Promise<HolidayResponse[]> {
  return performTrackedRequest(REQUEST_HOLIDAY_LIST_ID, async () => {
    const response = await APIKit.get(buildHolidayUrl(params));
    return response.data as HolidayResponse[];
  });
}

export async function holidayCreate(data: HolidayRequest): Promise<HolidayResponse> {
  return performTrackedRequest(REQUEST_HOLIDAY_CREATE_ID, async () => {
    const response = await APIKit.post(API_V1_HOLIDAY, data);
    return response.data as HolidayResponse;
  });
}

export async function holidayUpdate(id: number, data: HolidayRequest): Promise<HolidayResponse> {
  return performTrackedRequest(buildHolidayUpdateRequestId(id), async () => {
    const response = await APIKit.put(`${API_V1_HOLIDAY}/${id}`, data);
    return response.data as HolidayResponse;
  });
}

export async function holidayDelete(id: number): Promise<void> {
  await performTrackedRequest(buildHolidayDeleteRequestId(id), async () => {
    await APIKit.delete(`${API_V1_HOLIDAY}/${id}`);
    return undefined;
  });
}
